import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/min';

export interface Country {
  /** Country display name */
  name: string;
  /** ISO 3166-1 alpha-2 code (lowercase) — used to load the flag image */
  iso: string;
  /** International dial code, including the leading "+" */
  dialCode: string;
}

// Resolve human-readable country names from ISO codes using the native Intl
// API (no extra dependency). Falls back to the ISO code if unavailable.
const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

/**
 * Full list of countries with their international dial codes, derived from
 * libphonenumber-js (the maintained port of Google's libphonenumber). This
 * stays in sync with the library instead of being hand-maintained.
 *
 * Flags are rendered from flagcdn.com using the ISO code, so they display
 * correctly on every platform (emoji flags don't render on Windows browsers).
 */
export const COUNTRIES: Country[] = getCountries()
  .map((code) => ({
    name: regionNames?.of(code) ?? code,
    iso: code.toLowerCase(),
    dialCode: `+${getCountryCallingCode(code)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/** Default country used by phone inputs (India). */
export const DEFAULT_COUNTRY: Country =
  COUNTRIES.find((c) => c.iso === 'in') ?? COUNTRIES[0];

/** Build the flag image URL for a country from flagcdn.com. */
export function flagUrl(iso: string): string {
  return `https://flagcdn.com/w40/${iso}.png`;
}

/**
 * Split a stored full phone number (e.g. "+919876543210") back into its
 * country and national-number parts so a phone input can be pre-filled.
 * Falls back to the default country when the value is empty or unparseable.
 */
export function parseStoredPhone(stored: string | null | undefined): {
  country: Country;
  nationalNumber: string;
} {
  if (!stored) return { country: DEFAULT_COUNTRY, nationalNumber: '' };

  const withPlus = stored.trim().startsWith('+') ? stored.trim() : `+${stored.replace(/\D/g, '')}`;

  const parsed = parsePhoneNumberFromString(withPlus);
  if (parsed) {
    const byIso = parsed.country
      ? COUNTRIES.find((c) => c.iso === parsed.country!.toLowerCase())
      : undefined;
    const byCode = COUNTRIES.find((c) => c.dialCode === `+${parsed.countryCallingCode}`);
    return { country: byIso ?? byCode ?? DEFAULT_COUNTRY, nationalNumber: parsed.nationalNumber };
  }

  // Fallback: strip the longest matching dial-code prefix.
  const match = [...COUNTRIES]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((c) => withPlus.startsWith(c.dialCode));
  if (match) {
    return { country: match, nationalNumber: withPlus.slice(match.dialCode.length).replace(/\D/g, '') };
  }
  return { country: DEFAULT_COUNTRY, nationalNumber: withPlus.replace(/\D/g, '') };
}
